import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, BellRing, Check, Plus, Search, X } from "lucide-react";
import { useMemo, useState } from "react";

import { Button, Card, CardHeader, Money, StatusPill } from "@/components/ui";
import { ApiError } from "@/lib/api";
import { currentCashTotals, searchProducts, type SearchHit } from "@/lib/endpoints";
import { newIdempotencyKey } from "@/lib/http";
import { getSession } from "@/lib/session";
import {
  chargeOrder,
  decideEstimate,
  getOrder,
  notifyCustomer,
  saveEstimate,
  sendEstimate,
  transitionOrder,
  updateChecklistItem,
  STATUS_LABEL,
  STATUS_TONE,
  type EstimateLineInput,
} from "@/lib/workshop";

import { PaymentDialog } from "@/features/pos/PaymentDialog";
import { ReceiptDialog } from "@/features/pos/ReceiptDialog";

/**
 * Detalle de una orden de reparación.
 *
 * La pantalla está ordenada como trabaja un taller: a la izquierda **qué ha
 * pasado** (la historia y el checklist), a la derecha **qué toca ahora** (el
 * presupuesto y los botones). Los botones no están escritos aquí: llegan del
 * servidor en `available_transitions`, ya filtrados por permisos y con su
 * etiqueta. Si el técnico entra, ve «Empezar diagnóstico»; si entra el cajero,
 * ve «Cobrar». La misma pantalla, sin `if` de rol repartidos por el código.
 */
export function OrderDetailScreen({
  orderId,
  onBack,
}: {
  orderId: string;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [nuevaLinea, setNuevaLinea] = useState(false);
  const [cobrando, setCobrando] = useState(false);
  const [recibo, setRecibo] = useState<{ folio: string; saleId: string; total: number } | null>(null);
  const [notaTransicion, setNotaTransicion] = useState<{ to: string; label: string } | null>(null);

  const orden = useQuery({
    queryKey: ["workshop", "order", orderId],
    queryFn: () => getOrder(orderId),
  });

  function refrescar() {
    void queryClient.invalidateQueries({ queryKey: ["workshop"] });
  }

  function onError(cause: unknown) {
    setError(cause instanceof ApiError ? cause.message : "No se pudo completar la operación");
  }

  const mover = useMutation({
    mutationFn: ({ to, notes }: { to: string; notes?: string }) =>
      transitionOrder(orderId, to, notes),
    onSuccess: () => {
      setNotaTransicion(null);
      setError(null);
      refrescar();
    },
    onError,
  });

  const marcar = useMutation({
    mutationFn: ({ itemId, done }: { itemId: string; done: boolean }) =>
      updateChecklistItem(orderId, itemId, done),
    onSuccess: refrescar,
    onError,
  });

  const enviar = useMutation({
    mutationFn: () => sendEstimate(orderId),
    onSuccess: refrescar,
    onError,
  });

  const decidir = useMutation({
    mutationFn: ({ approved, note }: { approved: boolean; note?: string }) =>
      decideEstimate(orderId, approved, note),
    onSuccess: refrescar,
    onError,
  });

  const avisar = useMutation({
    mutationFn: () => notifyCustomer(orderId, "whatsapp"),
    onSuccess: (aviso) => {
      setError(
        aviso.status === "enviada"
          ? null
          : `Aviso registrado pero no enviado: ${aviso.error ?? "sin proveedor configurado"}`,
      );
      refrescar();
    },
    onError,
  });

  const detalle = orden.data;

  return (
    <div className="space-y-5 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" onClick={onBack} icon={<ArrowLeft aria-hidden className="size-4" />}>
            Tablero
          </Button>
          {detalle ? (
            <div>
              <p className="font-mono text-xs text-muted">
                Orden {detalle.order.series}-{detalle.order.folio}
              </p>
              <h1 className="font-display text-xl font-bold text-carbon">
                {detalle.order.brand} {detalle.order.model}
              </h1>
              <p className="text-sm text-muted">
                {detalle.customer.name}
                {detalle.customer.phone ? ` · ${detalle.customer.phone}` : ""}
              </p>
            </div>
          ) : null}
        </div>

        {detalle ? (
          <div className="flex items-center gap-2">
            <StatusPill tone={STATUS_TONE[detalle.order.status]}>
              {STATUS_LABEL[detalle.order.status] ?? detalle.order.status}
            </StatusPill>
            {detalle.order.warranty_until ? (
              <StatusPill tone="info">Garantía hasta {detalle.order.warranty_until}</StatusPill>
            ) : null}
          </div>
        ) : null}
      </header>

      {error ? (
        <p role="alert" className="rounded-msr border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
          {error}
        </p>
      ) : null}

      {orden.isPending ? (
        <p className="text-muted">Cargando la orden…</p>
      ) : !detalle ? (
        <p className="text-danger">No se encontró la orden.</p>
      ) : (
        <div className="grid gap-5 xl:grid-cols-[1fr_26rem]">
          {/* ------------------- Qué ha pasado ------------------- */}
          <div className="space-y-5">
            <Card>
              <CardHeader
                title="El equipo"
                description={`${detalle.order.device_type}${
                  detalle.order.serial_number ? ` · serie ${detalle.order.serial_number}` : ""
                }`}
              />
              <dl className="space-y-2 p-4 text-sm">
                <Row label="Avería según el cliente">{detalle.order.reported_issue}</Row>
                {detalle.order.accessories ? (
                  <Row label="Accesorios">{detalle.order.accessories}</Row>
                ) : null}
                {detalle.order.physical_condition ? (
                  <Row label="Estado al recibir">{detalle.order.physical_condition}</Row>
                ) : null}
                {detalle.order.unlock_code ? (
                  <Row label="Clave">{detalle.order.unlock_code}</Row>
                ) : null}
                {detalle.order.diagnosis ? (
                  <Row label="Diagnóstico">{detalle.order.diagnosis}</Row>
                ) : null}
              </dl>
            </Card>

            {(detalle.checklist ?? []).length > 0 ? (
              <Card>
                <CardHeader
                  title="Checklist"
                  description="Los puntos obligatorios frenan el paso a «listo»."
                />
                <ul className="divide-y divide-line">
                  {(detalle.checklist ?? []).map((item) => (
                    <li key={item.id} className="flex items-center gap-3 px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={item.done}
                        onChange={(event) =>
                          marcar.mutate({ itemId: item.id, done: event.target.checked })
                        }
                        aria-label={item.label}
                        className="size-4 accent-[var(--color-electric)]"
                      />
                      <span className={item.done ? "text-muted line-through" : "text-carbon"}>
                        {item.label}
                      </span>
                      {item.required ? (
                        <span className="ml-auto text-[10px] tracking-wide text-muted uppercase">
                          obligatorio
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}

            <Card>
              <CardHeader
                title="Historia"
                description="Cada cambio con quién lo hizo. No se puede reescribir."
              />
              <ol className="space-y-3 p-4">
                {(detalle.history ?? []).map((paso) => (
                  <li key={paso.id} className="flex gap-3 text-sm">
                    <span className="mt-1.5 size-2 shrink-0 rounded-full bg-electric" />
                    <div>
                      <p className="font-medium text-carbon">{paso.label}</p>
                      <p className="text-xs text-muted">
                        {new Date(paso.changed_at).toLocaleString("es-MX")}
                        {paso.from_status ? ` · ${paso.from_status} → ${paso.to_status}` : ""}
                      </p>
                      {paso.notes ? (
                        <p className="mt-1 text-xs text-muted">{paso.notes}</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          </div>

          {/* ------------------- Qué toca ahora ------------------- */}
          <div className="space-y-5">
            <Card>
              <CardHeader
                title="Presupuesto"
                description={
                  detalle.estimate
                    ? `Versión ${detalle.estimate.version} · ${detalle.estimate.status}`
                    : "Sin presupuesto todavía"
                }
                actions={
                  detalle.estimate ? (
                    <StatusPill
                      tone={
                        detalle.estimate.status === "aprobado"
                          ? "ok"
                          : detalle.estimate.status === "rechazado"
                            ? "warning"
                            : "neutral"
                      }
                    >
                      {detalle.estimate.status}
                    </StatusPill>
                  ) : null
                }
              />

              <div className="space-y-2 p-4">
                {(detalle.estimate?.lines ?? []).map((line) => (
                  <div key={line.id} className="flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <p className="truncate text-carbon font-medium">{line.description}</p>
                      <p className="text-xs text-muted">
                        {line.kind.replace("_", " ")} · {line.quantity} ×{" "}
                        <Money cents={line.unit_price_cents} size="sm" />
                      </p>
                    </div>
                    <Money cents={line.total_cents} size="sm" className="font-semibold text-carbon" />
                  </div>
                ))}

                {detalle.estimate ? (
                  <dl className="space-y-1 border-t border-line pt-2 text-sm">
                    <Row label="Subtotal">
                      <Money cents={detalle.estimate.subtotal_cents} size="sm" />
                    </Row>
                    <Row label="IVA">
                      <Money cents={detalle.estimate.tax_cents} size="sm" />
                    </Row>
                    <Row label="Total">
                      <Money cents={detalle.estimate.total_cents} size="md" className="font-bold text-carbon" />
                    </Row>
                  </dl>
                ) : (
                  <p className="py-3 text-center text-sm text-muted">
                    Agrega mano de obra y repuestos para poder presupuestar.
                  </p>
                )}

                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => setNuevaLinea(true)}
                  icon={<Plus aria-hidden className="size-4" />}
                >
                  Agregar línea
                </Button>

                {detalle.estimate && detalle.estimate.status !== "aprobado" ? (
                  <Button
                    variant="ghost"
                    fullWidth
                    loading={enviar.isPending}
                    onClick={() => enviar.mutate()}
                  >
                    Marcar como enviado al cliente
                  </Button>
                ) : null}

                {detalle.estimate &&
                (detalle.estimate.status === "enviado" || detalle.estimate.status === "borrador") ? (
                  <div className="flex gap-2 pt-1">
                    <Button
                      fullWidth
                      loading={decidir.isPending}
                      onClick={() => decidir.mutate({ approved: true, note: "Aprobado por teléfono" })}
                      icon={<Check aria-hidden className="size-4" />}
                    >
                      Cliente aprobó
                    </Button>
                    <Button
                      variant="danger"
                      fullWidth
                      onClick={() => {
                        const motivo = window.prompt("¿Por qué lo rechazó?");
                        if (motivo) decidir.mutate({ approved: false, note: motivo });
                      }}
                      icon={<X aria-hidden className="size-4" />}
                    >
                      Rechazó
                    </Button>
                  </div>
                ) : null}
              </div>
            </Card>

            <Card>
              <CardHeader title="Qué hacer ahora" />
              <div className="space-y-2 p-4">
                {(detalle.available_transitions ?? []).length === 0 ? (
                  <p className="text-sm text-muted">
                    La orden está cerrada: no admite más cambios.
                  </p>
                ) : (
                  (detalle.available_transitions ?? []).map((opcion) => (
                    <Button
                      key={opcion.to}
                      fullWidth
                      variant={opcion.requires_note ? "secondary" : "primary"}
                      disabled={!opcion.allowed}
                      title={
                        opcion.allowed
                          ? opcion.label
                          : `Necesitas el permiso ${opcion.permission}`
                      }
                      loading={mover.isPending && mover.variables?.to === opcion.to}
                      onClick={() =>
                        opcion.requires_note
                          ? setNotaTransicion({ to: opcion.to, label: opcion.label })
                          : mover.mutate({ to: opcion.to })
                      }
                    >
                      {opcion.label}
                    </Button>
                  ))
                )}

                {detalle.can_charge ? (
                  <Button
                    size="lg"
                    fullWidth
                    onClick={() => setCobrando(true)}
                    icon={<Money cents={detalle.estimate?.total_cents ?? 0} size="sm" />}
                  >
                    Entregar y cobrar
                  </Button>
                ) : detalle.charge_blocked_reason ? (
                  <p className="rounded-msr border border-line px-3 py-2 text-xs text-muted">
                    {detalle.charge_blocked_reason}
                  </p>
                ) : null}

                <Button
                  variant="ghost"
                  fullWidth
                  loading={avisar.isPending}
                  onClick={() => avisar.mutate()}
                  icon={<BellRing aria-hidden className="size-4" />}
                >
                  Avisar al cliente
                </Button>
              </div>

              {(detalle.notifications ?? []).length > 0 ? (
                <ul className="space-y-1 border-t border-line p-4 text-xs">
                  {(detalle.notifications ?? []).map((aviso) => (
                    <li key={aviso.id} className="flex items-start gap-2">
                      <StatusPill tone={aviso.status === "enviada" ? "ok" : "warning"}>
                        {aviso.status}
                      </StatusPill>
                      <span className="text-muted">
                        {aviso.channel} · {aviso.target}
                        {aviso.error ? ` · ${aviso.error}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </Card>
          </div>
        </div>
      )}

      {nuevaLinea && detalle ? (
        <AddLineDialog
          onClose={() => setNuevaLinea(false)}
          onAdd={async (linea) => {
            setNuevaLinea(false);
            const existentes: EstimateLineInput[] = (detalle.estimate?.lines ?? []).map(
              (line) => ({
                kind: line.kind as EstimateLineInput["kind"],
                description: line.description,
                variant_id: line.variant_id,
                quantity: line.quantity,
                unit_price_cents: line.unit_price_cents,
                discount_cents: line.discount_cents,
              }),
            );
            try {
              await saveEstimate(orderId, [...existentes, linea]);
              setError(null);
              refrescar();
            } catch (cause) {
              onError(cause);
            }
          }}
        />
      ) : null}

      {notaTransicion && detalle ? (
        <ReasonDialog
          title={notaTransicion.label}
          onCancel={() => setNotaTransicion(null)}
          onConfirm={(nota) => mover.mutate({ to: notaTransicion.to, notes: nota })}
        />
      ) : null}

      {cobrando && detalle ? (
        <PaymentDialog
          totalCents={detalle.estimate?.total_cents ?? 0}
          onCancel={() => setCobrando(false)}
          onConfirm={async (pagos) => {
            try {
              // Se busca el turno abierto en vez de pedirlo en la pantalla: el
              // cajero ya lo abrió al empezar el día, y volver a elegirlo en
              // cada cobro es una pregunta cuya respuesta el sistema ya tiene.
              let turno: string | null = null;
              try {
                turno = (await currentCashTotals()).cash_session_id;
              } catch {
                turno = null; // sin turno abierto: el servidor lo dirá con su motivo
              }

              const resultado = await chargeOrder(
                orderId,
                {
                  cash_session_id: turno,
                  payments: pagos.map((pago) => ({
                    method: pago.method as "efectivo",
                    amount_cents: pago.amount_cents,
                    tip_cents: pago.tip_cents,
                  })),
                  delivered_to: detalle.customer.name,
                  warranty_days: detalle.order.warranty_days,
                },
                newIdempotencyKey("os"),
              );
              setCobrando(false);
              setRecibo({
                folio: resultado.folio,
                saleId: resultado.sale_id,
                total: resultado.total_cents,
              });
              setError(null);
              refrescar();
            } catch (cause) {
              setCobrando(false);
              onError(cause);
            }
          }}
        />
      ) : null}

      {recibo ? (
        <ReceiptDialog
          folio={recibo.folio}
          sale={null}
          onClose={() => setRecibo(null)}
        />
      ) : null}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="text-right text-carbon font-medium">{children}</dd>
    </div>
  );
}

/** Añadir mano de obra libre o un repuesto/servicio del catálogo. */
function AddLineDialog({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (linea: EstimateLineInput) => void;
}) {
  const branch = getSession()?.branches[0] ?? null;
  const [kind, setKind] = useState<"mano_obra" | "repuesto" | "servicio">("repuesto");
  const [term, setTerm] = useState("");
  const [resultados, setResultados] = useState<SearchHit[]>([]);
  const [elegido, setElegido] = useState<SearchHit | null>(null);
  const [descripcion, setDescripcion] = useState("");
  const [cantidad, setCantidad] = useState("1");
  const [precio, setPrecio] = useState("");

  const buscar = useMemo(
    () => async (texto: string) => {
      if (texto.trim().length < 2) {
        setResultados([]);
        return;
      }
      try {
        const encontrados = await searchProducts(texto, branch?.id);
        setResultados(encontrados.items);
      } catch {
        setResultados([]);
      }
    },
    [branch?.id],
  );

  const precioCentavos = precio ? Math.round(Number.parseFloat(precio.replace(",", ".")) * 100) : null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div className="w-full max-w-xl space-y-4 rounded-msr-lg border border-line bg-snow p-6 text-carbon shadow-2xl">
        <h2 className="font-display text-lg font-bold text-carbon">Agregar al presupuesto</h2>

        <div className="grid grid-cols-3 gap-2">
          {(["mano_obra", "repuesto", "servicio"] as const).map((opcion) => (
            <button
              key={opcion}
              type="button"
              onClick={() => {
                setKind(opcion);
                setElegido(null);
              }}
              className={`rounded-msr border px-3 py-2 text-sm capitalize transition-colors ${
                kind === opcion
                  ? "border-electric bg-electric/15 text-carbon font-semibold"
                  : "border-line bg-surface/50 text-muted hover:bg-surface"
              }`}
            >
              {opcion.replace("_", " ")}
            </button>
          ))}
        </div>

        {kind === "mano_obra" ? (
          <label className="block">
            <span className="mb-1 block text-xs text-muted uppercase">Descripción</span>
            <input
              value={descripcion}
              onChange={(event) => setDescripcion(event.target.value)}
              placeholder="Cambio de pantalla"
              className="focus:border-electric h-11 w-full rounded-msr border border-line bg-snow px-3 text-carbon placeholder:text-muted focus:outline-none"
            />
          </label>
        ) : (
          <>
            <label className="relative block">
              <span className="mb-1 block text-xs text-muted uppercase">
                Buscar en el catálogo
              </span>
              <Search aria-hidden className="absolute top-9 left-3 size-4 text-muted" />
              <input
                value={term}
                onChange={(event) => {
                  setTerm(event.target.value);
                  void buscar(event.target.value);
                }}
                placeholder="Nombre o código"
                className="focus:border-electric h-11 w-full rounded-msr border border-line bg-snow pr-3 pl-9 text-carbon placeholder:text-muted focus:outline-none"
              />
            </label>

            {elegido ? (
              <p className="rounded-msr border border-electric/40 bg-electric/10 px-3 py-2 text-sm text-carbon font-medium">
                {elegido.product_name} · <Money cents={elegido.price_cents ?? 0} size="sm" />
              </p>
            ) : (
              <ul className="max-h-40 space-y-1 overflow-auto">
                {resultados.map((hit) => (
                  <li key={hit.variant_id}>
                    <button
                      type="button"
                      onClick={() => {
                        setElegido(hit);
                        setDescripcion(hit.product_name);
                        setPrecio(((hit.price_cents ?? 0) / 100).toFixed(2));
                      }}
                      className="flex w-full items-center justify-between rounded-msr border border-line px-3 py-2 text-left text-sm text-carbon hover:bg-surface"
                    >
                      <span>{hit.product_name}</span>
                      <Money cents={hit.price_cents ?? 0} size="sm" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs text-muted uppercase">Cantidad</span>
            <input
              value={cantidad}
              onChange={(event) => setCantidad(event.target.value)}
              inputMode="decimal"
              className="focus:border-electric h-11 w-full rounded-msr border border-line bg-snow px-3 text-right text-carbon focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted uppercase">Precio unitario</span>
            <input
              value={precio}
              onChange={(event) => setPrecio(event.target.value)}
              inputMode="decimal"
              placeholder="0.00"
              className="focus:border-electric h-11 w-full rounded-msr border border-line bg-snow px-3 text-right text-carbon placeholder:text-muted focus:outline-none"
            />
          </label>
        </div>

        <div className="flex gap-3">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancelar
          </Button>
          <Button
            fullWidth
            disabled={kind !== "mano_obra" && !elegido}
            onClick={() =>
              onAdd({
                kind,
                description: descripcion,
                variant_id: elegido?.variant_id ?? null,
                quantity: cantidad,
                unit_price_cents: kind === "repuesto" && !precio ? null : precioCentavos,
                discount_cents: 0,
              })
            }
          >
            Agregar
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReasonDialog({
  title,
  onCancel,
  onConfirm,
}: {
  title: string;
  onCancel: () => void;
  onConfirm: (nota: string) => void;
}) {
  const [nota, setNota] = useState("");
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div className="w-full max-w-md space-y-4 rounded-msr-lg border border-line bg-snow p-6 text-carbon shadow-2xl">
        <h2 className="font-display text-lg font-bold text-carbon">{title}</h2>
        <p className="text-sm text-muted">
          Queda guardado en la historia de la orden: es lo que permitirá explicarla después.
        </p>
        <textarea
          autoFocus
          value={nota}
          onChange={(event) => setNota(event.target.value)}
          rows={3}
          placeholder="El cliente no volvió, faltó el repuesto…"
          className="focus:border-electric w-full rounded-msr border border-line bg-snow px-3 py-2 text-carbon placeholder:text-muted focus:outline-none"
        />
        <div className="flex gap-3">
          <Button variant="secondary" fullWidth onClick={onCancel}>
            Cancelar
          </Button>
          <Button fullWidth disabled={nota.trim().length < 3} onClick={() => onConfirm(nota)}>
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  );
}

